import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { AuthShell } from '../../components/AuthShell';

import {
  API_BASE_URL,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_LOGOUT_URI,
  COGNITO_REDIRECT_URI,
} from '../../config';

type Tokens = {
  id_token: string;
  access_token: string;
  refresh_token?: string;
};

type InvitationValidateResponse = {
  email: string;
  orgName: string;
  tenantId: string;
  role: string;
};

type PendingInvitesResponse = {
  invitations?: Array<{ token: string; tenantId: string; orgName: string; role: string }>;
};

type OrganizationsCheckResponse = {
  tenantId: string;
  orgName?: string;
  role?: string;
  plan?: string;
};

type UserMeResponse = {
  tenantId: string;
  orgName: string;
  userId: string;
  name?: string;
  role: string;
  plan?: string;
};

function base64UrlToBase64(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  return base64 + '='.repeat(padLength);
}

function decodeJwtPayload(token: string): any {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const base64 = base64UrlToBase64(parts[1]);
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const jsonPayload = new TextDecoder().decode(bytes);
  return JSON.parse(jsonPayload);
}

async function exchangeCodeForTokens(code: string): Promise<Tokens> {
  if (!COGNITO_DOMAIN) throw new Error('Cognito domain is not configured');

  const tokenResponse = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      code,
      redirect_uri: COGNITO_REDIRECT_URI,
    }),
  });

  if (!tokenResponse.ok) throw new Error('Token exchange failed');
  return (await tokenResponse.json()) as Tokens;
}

export const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const toast = useToast();

  const debugAuthEnabled =
    searchParams.get('debugAuth') === '1' ||
    (() => {
      try {
        return localStorage.getItem('nimbus:debugAuth') === '1';
      } catch {
        return false;
      }
    })();

  const maskedClientId = (() => {
    const raw = COGNITO_CLIENT_ID || '';
    if (raw.length <= 10) return raw;
    return `${raw.slice(0, 6)}…${raw.slice(-4)}`;
  })();

  useEffect(() => {
    const run = async () => {
      const oauthError = searchParams.get('error');
      const code = searchParams.get('code');
      const invitationFromUrl = searchParams.get('invitation');

      if (oauthError) {
        setError(`Authentication failed: ${oauthError}`);
        navigate('/login');
        return;
      }

      if (!code) {
        navigate('/login');
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code);
        const payload = decodeJwtPayload(tokens.id_token);

        if (!payload) throw new Error('Failed to parse ID token');

        localStorage.setItem('idToken', tokens.id_token);
        localStorage.setItem('accessToken', tokens.access_token);
        if (tokens.refresh_token) localStorage.setItem('refreshToken', tokens.refresh_token);

        localStorage.setItem('userEmail', payload.email || '');
        const displayName = payload.email || payload['cognito:username'] || '';
        localStorage.setItem('userName', displayName);
        localStorage.setItem('userId', payload.sub || '');

        const groups: string[] = payload['cognito:groups'] || [];
        localStorage.setItem('isSuperAdmin', groups.includes('salvinta-superadmin') ? 'true' : 'false');

        const orgNameAttr = payload['custom:orgName'];
        const roleAttr = payload['custom:role'];
        const cognitoSub = payload.sub;

        const invitationToken =
          sessionStorage.getItem('pendingInvitation') ||
          sessionStorage.getItem('invitationToken') ||
          invitationFromUrl ||
          '';

        // If invitation token exists, validate + accept
        if (invitationToken) {
          try {
            const inviteResponse = await fetch(
              `${API_BASE_URL}/invitations/validate?token=${encodeURIComponent(invitationToken)}`,
              {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${tokens.id_token}`,
                  'Content-Type': 'application/json',
                },
              }
            );

            if (inviteResponse.ok) {
              const invitation = (await inviteResponse.json()) as InvitationValidateResponse;

              if ((invitation.email || '').toLowerCase() !== (payload.email || '').toLowerCase()) {
                toast.error('This invitation was sent to a different email address. Please use the correct email to sign up.');
                navigate('/login');
                return;
              }

              const acceptResponse = await fetch(`${API_BASE_URL}/organizations`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${tokens.id_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  invitation: invitationToken,
                  cognitoSub,
                  adminEmail: payload.email,
                  adminName: payload.name,
                }),
              });

              if (!acceptResponse.ok) {
                const acceptErr = await acceptResponse.json().catch(() => ({}));
                throw new Error((acceptErr as any).error || 'Failed to accept invitation');
              }

              localStorage.setItem('tenantId', invitation.tenantId);
              localStorage.setItem('orgName', invitation.orgName);
              localStorage.setItem('userRole', invitation.role);
              sessionStorage.removeItem('invitationToken');
              sessionStorage.removeItem('pendingInvitation');

              navigate('/dashboard?joined=true');
              return;
            } else {
              sessionStorage.removeItem('invitationToken');
              sessionStorage.removeItem('pendingInvitation');
            }
          } catch (inviteError) {
            console.error('Error processing invitation:', inviteError);
            sessionStorage.removeItem('invitationToken');
            sessionStorage.removeItem('pendingInvitation');
          }
        }

        // If no explicit invitation token, check pending invitations by email
        try {
          const pendingInvitesResponse = await fetch(
            `${API_BASE_URL}/invitations/pending?email=${encodeURIComponent(payload.email || '')}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokens.id_token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (pendingInvitesResponse.ok) {
            const pendingInvites = (await pendingInvitesResponse.json()) as PendingInvitesResponse;
            const first = pendingInvites.invitations?.[0];
            if (first?.token) {
              const acceptResponse = await fetch(`${API_BASE_URL}/organizations`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${tokens.id_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  invitation: first.token,
                  cognitoSub,
                  adminEmail: payload.email,
                  adminName: payload.name || payload.email,
                }),
              });

              if (acceptResponse.ok) {
                localStorage.setItem('tenantId', first.tenantId);
                localStorage.setItem('orgName', first.orgName);
                localStorage.setItem('userRole', first.role);
                navigate('/dashboard?joined=true');
                return;
              }
            }
          }
        } catch {
          // ignore
        }

        // Check if user already exists
        try {
          const checkResponse = await fetch(
            `${API_BASE_URL}/organizations/check?cognitoSub=${encodeURIComponent(cognitoSub)}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokens.id_token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (checkResponse.status === 200) {
            const userData = (await checkResponse.json()) as OrganizationsCheckResponse;

            const storedInvitation = sessionStorage.getItem('pendingInvitation');
            if (storedInvitation) {
              try {
                const inviteCheckResponse = await fetch(
                  `${API_BASE_URL}/invitations/validate?token=${encodeURIComponent(storedInvitation)}`
                );
                if (inviteCheckResponse.ok) {
                  const inviteData = (await inviteCheckResponse.json()) as InvitationValidateResponse;
                  if (inviteData.tenantId !== userData.tenantId) {
                    if (confirm(`You're being invited to join ${inviteData.orgName}. This will switch your organization. Continue?`)) {
                      const switchResponse = await fetch(`${API_BASE_URL}/organizations`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${tokens.id_token}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          invitation: storedInvitation,
                          cognitoSub,
                          adminEmail: payload.email,
                          adminName: payload.name || payload.email,
                        }),
                      });

                      if (switchResponse.ok) {
                        localStorage.setItem('tenantId', inviteData.tenantId);
                        localStorage.setItem('orgName', inviteData.orgName);
                        localStorage.setItem('userRole', inviteData.role);
                        sessionStorage.removeItem('pendingInvitation');
                        navigate('/dashboard?joined=true');
                        return;
                      }
                    }
                  }
                }
              } catch {
                // ignore
              }
              sessionStorage.removeItem('pendingInvitation');
            }

            localStorage.setItem('tenantId', userData.tenantId);
            localStorage.setItem('orgName', orgNameAttr || userData.orgName || '');
            localStorage.setItem('userRole', roleAttr || userData.role || 'user');
            localStorage.setItem('userPlan', userData.plan || 'basic');
            navigate('/dashboard');
            return;
          }

          if (checkResponse.status === 404) {
            const userMeResponse = await fetch(`${API_BASE_URL}/users/me`, {
              headers: {
                Authorization: `Bearer ${tokens.id_token}`,
                'Content-Type': 'application/json',
              },
            });

            if (userMeResponse.ok) {
              const me = (await userMeResponse.json()) as UserMeResponse;
              localStorage.setItem('tenantId', me.tenantId);
              localStorage.setItem('orgName', me.orgName);
              localStorage.setItem('userId', me.userId);
              localStorage.setItem('userName', me.name || payload.name || payload.email || '');
              localStorage.setItem('userRole', me.role);
              localStorage.setItem('userPlan', me.plan || 'basic');
              navigate('/dashboard');
              return;
            }

            if (userMeResponse.status === 404) {
              // Authenticated but no tenant record — this deployment is invite-only,
              // so there's no self-serve signup path to send them to.
              navigate('/no-access');
              return;
            }

            throw new Error('Failed to fetch user profile');
          }

          throw new Error('Authentication error');
        } catch (apiError) {
          console.error('Error checking user status:', apiError);
          setError('Authentication error. Please try again or contact support.');
          navigate('/login');
          return;
        }
      } catch (e: any) {
        console.error('Authentication error:', e);
        setError(e?.message || 'Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    void run();
  }, [navigate, searchParams]);

  return (
    <AuthShell maxWidth={480}>
      <div className="text-center">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="np-sub mb-0">Signing you in…</p>
      </div>

      {debugAuthEnabled ? (
        <div className="np-banner np-tone-warning mt-4" style={{ display: 'block', textAlign: 'left' }}>
          <div className="fw-semibold mb-1">Auth debug (opt-in)</div>
          <div className="small">
            <div>
              <strong>Has code param:</strong> {searchParams.get('code') ? 'yes' : 'no'}
            </div>
            <div>
              <strong>Has error param:</strong> {searchParams.get('error') ? 'yes' : 'no'}
            </div>
            <div>
              <strong>COGNITO_DOMAIN:</strong> {COGNITO_DOMAIN || '(not set)'}
            </div>
            <div>
              <strong>COGNITO_REDIRECT_URI:</strong> {COGNITO_REDIRECT_URI || '(not set)'}
            </div>
            <div>
              <strong>COGNITO_LOGOUT_URI:</strong> {COGNITO_LOGOUT_URI || '(not set)'}
            </div>
            <div>
              <strong>COGNITO_CLIENT_ID:</strong> {maskedClientId || '(not set)'}
            </div>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="np-banner np-tone-danger mt-4" style={{ display: 'block' }}>
          {error}
          <div className="mt-3 d-flex gap-2">
            <a className="np-btn np-btn-ghost np-btn-sm" href="/login">
              Back to sign in
            </a>
            {COGNITO_DOMAIN ? (
              <a
                className="np-btn np-btn-ghost np-btn-sm"
                href={`https://${COGNITO_DOMAIN}/logout?client_id=${encodeURIComponent(
                  COGNITO_CLIENT_ID
                )}&logout_uri=${encodeURIComponent(COGNITO_LOGOUT_URI)}`}
              >
                Logout
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </AuthShell>
  );
};
